<template>
  <header class="header">
    <!-- PRIMARY BAR: Dark blue with logo, search, utility items -->
    <div class="header-primary">
      <div class="container header-primary__inner">
        <nuxt-link to="/" class="header-primary__logo">
          <img
            src="../../assets/images/Vixio_Logo_White.svg"
            alt="Payments Compliance"
          />
        </nuxt-link>
        <div class="header-primary__search" :class="{ show: showSearch }">
          <Search />
        </div>
        <div class="header-primary__right">
          <!-- Regulatory Data product switcher -->
          <div
            v-if="isAuthorize"
            class="header-utility"
            @mouseover="regDataOpen = true"
            @mouseleave="regDataOpen = false"
          >
            <button class="header-utility__btn">
              REGULATORY DATA
              <svg class="header-utility__chevron" :class="{ open: regDataOpen }" width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div v-show="regDataOpen" class="header-utility__dropdown">
              <span class="header-utility__dropdown-current">Payments Compliance</span>
              <a
                v-if="otherVerticalSubs.financial_services && otherVerticalSubs.financial_services.active"
                :href="fsUrl"
                class="header-utility__dropdown-item"
              >
                Financial Services
              </a>
              <span v-else class="header-utility__dropdown-item header-utility__dropdown-item--disabled">
                Financial Services
              </span>
              <a href="#" class="header-utility__dropdown-item header-utility__dropdown-item--disabled">
                GamblingCompliance
              </a>
            </div>
          </div>
          <!-- Workspace dropdown -->
          <div
            v-if="isAuthorize"
            class="header-utility"
            @mouseover="workspaceOpen = true"
            @mouseleave="workspaceOpen = false"
          >
            <button class="header-utility__btn" @click="workspaceAccess ? null : undefined">
              WORKSPACE
              <svg class="header-utility__chevron" :class="{ open: workspaceOpen }" width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div v-show="workspaceOpen" class="header-utility__dropdown">
              <template v-if="workspaceAccess">
                <a href="https://ws.vixio.com/triage" target="_blank" rel="noopener noreferrer" class="header-utility__dropdown-item">Triage</a>
                <a href="https://ws.vixio.com/boards" target="_blank" rel="noopener noreferrer" class="header-utility__dropdown-item">Action Boards</a>
                <a href="https://ws.vixio.com/obligations" target="_blank" rel="noopener noreferrer" class="header-utility__dropdown-item">Obligations Library</a>
              </template>
              <template v-else>
                <span class="header-utility__dropdown-item header-utility__dropdown-item--disabled">Triage</span>
                <span class="header-utility__dropdown-item header-utility__dropdown-item--disabled">Action Boards</span>
                <span class="header-utility__dropdown-item header-utility__dropdown-item--disabled">Obligations Library</span>
              </template>
            </div>
          </div>
          <!-- User icon / profile -->
          <div
            v-if="isAuthorize"
            class="header-utility"
            @mouseover="profileOpen = true"
            @mouseleave="profileOpen = false"
          >
            <button class="header-utility__btn header-utility__btn--icon">
              <svg width="20" height="20" viewBox="0 0 18 18" fill="none"><path d="M16.572,11.426a8.431,8.431,0,0,0-2.15-1.346.7.7,0,1,0-.571,1.285,7.031,7.031,0,0,1,1.793,1.117,2.8,2.8,0,0,1,.95,2.107v1.3a.7.7,0,0,1-.7.7H2.109a.7.7,0,0,1-.7-.7v-1.3a2.8,2.8,0,0,1,.95-2.107c.71-.624,2.78-2.076,6.644-2.076a5.209,5.209,0,1,0-3.169-1.08,9.775,9.775,0,0,0-4.4,2.1A4.21,4.21,0,0,0,0,14.59v1.3A2.112,2.112,0,0,0,2.109,18H15.891A2.112,2.112,0,0,0,18,15.891v-1.3A4.21,4.21,0,0,0,16.572,11.426ZM5.2,5.2A3.8,3.8,0,1,1,9,9,3.8,3.8,0,0,1,5.2,5.2Z" fill="currentColor"/></svg>
            </button>
            <div v-show="profileOpen" class="header-utility__dropdown">
              <nuxt-link
                v-for="(item, index) in profileMenu.submenu"
                :key="index"
                :to="item.link"
                class="header-utility__dropdown-item"
              >
                {{ item.title }}
              </nuxt-link>
              <button class="header-utility__dropdown-item header-utility__dropdown-item--logout" @click="handleLogout">
                Logout
              </button>
            </div>
          </div>
          <nuxt-link v-if="!isAuthorize" to="/login" class="header-utility__btn header-utility__btn--login">
            LOGIN
          </nuxt-link>
        </div>
        <!-- Mobile buttons -->
        <div class="header-primary__mobile">
          <div
            v-if="!showSearch"
            class="header-primary__mobile-search"
            @click="showSearch = true"
          >
            <Icon :icon="'search-black'" />
          </div>
          <div
            v-if="showSearch"
            class="header-primary__mobile-close"
            @click="showSearch = false"
          >
            <img src="@/assets/icons/i_close.svg" alt="close" />
          </div>
          <div v-else class="header-primary__mobile-burger">
            <Burger :props-handle-click="openSidebar" />
          </div>
        </div>
      </div>
    </div>
    <!-- SECONDARY BAR: Nav items + Tools -->
    <div class="header-secondary">
      <div class="container header-secondary__inner">
        <nav class="header-secondary__nav">
          <ul class="header-secondary__list">
            <li
              v-for="(item, index) in menu"
              :key="index"
              class="header-secondary__item"
              :class="{ 'header-secondary__item--restricted': checkAccess(item.title) }"
              @mouseover="onOver(index)"
              @mouseleave="onLeave"
            >
              <a v-if="item.tag" :href="item.link" target="_blank" rel="noopener noreferrer">
                {{ item.title }}
              </a>
              <nuxt-link v-else :to="item.link" :exact="item.link === '/'">
                {{ item.title }}
              </nuxt-link>
              <transition name="slide">
                <ul
                  v-if="item.submenu && activePoint === index"
                  class="header-secondary__submenu"
                >
                  <li v-for="(item2, k) in item.submenu" :key="k">
                    <nuxt-link :to="item2.link">
                      {{ item2.title }}
                    </nuxt-link>
                  </li>
                </ul>
              </transition>
              <Tooltip
                v-if="checkAccess(item.title)"
                :size="'small'"
                class="tip"
              />
            </li>
          </ul>
        </nav>
        <!-- VIQ Link -->
        <nuxt-link
          v-if="isAuthorize && viqEnabled"
          to="/viq"
          class="header-secondary__viq-link"
          title="VIQ — AI Research Assistant"
        >
          <img
            src="@/assets/images/viq-logo.svg"
            alt="VIQ"
            class="header-secondary__viq-logo"
          />
        </nuxt-link>
        <!-- Tools dropdown in secondary bar -->
        <div
          v-if="isAuthorize"
          class="header-secondary__tools"
          @mouseover="toolsOpen = true"
          @mouseleave="toolsOpen = false"
        >
          <button class="header-secondary__tools-btn">
            MY TOOLS
            <svg class="header-secondary__tools-chevron" :class="{ open: toolsOpen }" width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div v-show="toolsOpen" class="header-secondary__tools-dropdown">
            <nuxt-link v-if="hasAccessTools" to="/reportbuilder" class="header-secondary__tools-item">
              Report Builder
            </nuxt-link>
            <span v-else class="header-secondary__tools-item header-secondary__tools-item--disabled">Report Builder</span>
            <nuxt-link v-if="hasAccessTools" to="/reportbuilder-us" class="header-secondary__tools-item">
              Report Builder (US)
            </nuxt-link>
            <span v-else class="header-secondary__tools-item header-secondary__tools-item--disabled">Report Builder (US)</span>
          </div>
        </div>
      </div>
    </div>
    <MobileHeader v-if="isOpen" @close="isOpen = false" />
  </header>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import logout from "../../utils/logout";
import { checkAccess } from "@/utils/constants";
import { menu, profileMenu, reportsMenu } from "@/config";

export default {
  name: "Header",
  components: {
    MobileHeader: () => import("./MobileHeader"),
    Search: () => import("../elements/Search"),
    Icon: () => import("../elements/Icon"),
    Burger: () => import("../elements/Burger"),
    Tooltip: () => import("../elements/Tooltip"),
  },
  data() {
    return {
      menu,
      profileMenu,
      reportsMenu,
      isOpen: false,
      activePoint: null,
      showSearch: false,
      regDataOpen: false,
      workspaceOpen: false,
      profileOpen: false,
      toolsOpen: false,
    };
  },
  async fetch() {
    const otherVerticalSubs = this.$store.state.Verticals.otherVerticalSubs;
    if (otherVerticalSubs && Object.keys(otherVerticalSubs).length > 0) {
      return;
    }
    await this.$store.dispatch("Verticals/getOtherVerticalSubs", { vertical: "financial_services" });
  },
  computed: {
    ...mapState("Auth", ["isAuthorize", "subscriptions"]),
    ...mapState("Profile", ["userAccess", "dataUser"]),
    ...mapState("Verticals", ["otherVerticalSubs"]),
    ...mapGetters("Auth", ["workspaceAccess"]),
    viqEnabled() {
      // Master feature flag — when off, VIQ is hidden everywhere
      // and the legacy Pickaxe widget is shown instead.
      if (!this.$config.features || !this.$config.features.viq) return false;
      if (!this.subscriptions) return false;
      // Admins get VIQ regardless of company override
      const perms = this.subscriptions.permissions || [];
      const isAdmin = perms.includes("tool") && perms.includes("hs") && perms.includes("world");
      if (isAdmin) return true;
      // Non-admins: check company VIQ override
      if (this.subscriptions.viq_force_disabled) return false;
      return this.subscriptions.viq_override === true || perms.length > 0;
    },
    hasAccessTools(state) {
      if (
        state.subscriptions.legacy_permissions !== null &&
        state.subscriptions.legacy_permissions.length > 0
      )
        return true;
      return checkAccess(state.subscriptions.permissions, "tool");
    },
    fsUrl() {
      return this.$config.financialServicesFEURL
        ? this.$config.financialServicesFEURL
        : "https://fs.vixio.com/";
    },
  },
  mounted() {
    if (this.$churnZero && this.dataUser && this.dataUser.salesforceId && this.dataUser.companySfId) {
      this.$churnZero.setContact(this.dataUser.companySfId, this.dataUser.salesforceId);
    }
  },
  methods: {
    onOver(index) {
      this.activePoint = index;
    },
    onLeave() {
      this.activePoint = null;
    },
    openSidebar() {
      this.isOpen = true;
    },
    handleLogout() {
      this.$router.push("/login");
      logout(this);
    },
    checkAccess(item) {
      let access = true;
      if (
        this.subscriptions.permissions &&
        this.subscriptions.permissions.length === 0
      ) {
        return false;
      }
      if (
        this.subscriptions.permissions &&
        this.subscriptions.permissions.length > 0
      ) {
        access =
          (!this.subscriptions.permissions.includes("world") &&
            item === "World") ||
          (!this.subscriptions.permissions.includes("hs") &&
            item === "Horizon Scanning") ||
          (!this.subscriptions.permissions.includes("requirements") &&
            item === "Requirements");
      }
      return access;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../assets/styles/helpers/variables";
@import "../../assets/styles/helpers/mixin";

// Transitions
.slide-enter-active,
.slide-leave-active {
  transition: max-height 0.3s ease;
}
.slide-enter-to,
.slide-leave {
  max-height: 200px;
  overflow: hidden;
}
.slide-enter,
.slide-leave-to {
  max-height: 0;
  overflow: hidden;
}

// ============================================
// PRIMARY BAR
// ============================================
.header-primary {
  background: $dark_blue;
  padding: 0;
  position: relative;
  z-index: 11;

  &__inner {
    display: flex;
    align-items: center;
    height: 56px;
    gap: 24px;
  }

  &__logo {
    flex-shrink: 0;
    display: flex;
    align-items: center;

    img {
      width: 120px;
    }
  }

  &__search {
    flex: 1;
    max-width: 320px;

    @include below($md) {
      display: none;
      &.show {
        display: block;
        position: absolute;
        left: 0;
        right: 0;
        top: 56px;
        max-width: none;
        padding: 8px 16px;
        background: $dark_blue;
        z-index: 10;
      }
    }

    // Restyle the Search component for dark background
    ::v-deep {
      .search {
        input {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.18);
          color: $white;
          border-radius: 4px;
          font-size: 1.3rem;
          height: 36px;
          padding: 0 40px 0 14px;

          &::placeholder {
            color: rgba(255, 255, 255, 0.45);
          }

          &:focus {
            border-color: rgba(255, 255, 255, 0.35);
            background: rgba(255, 255, 255, 0.14);
            outline: none;
          }
        }

        button {
          height: 36px;
          width: 36px;
          background: transparent;
          border-radius: 0 4px 4px 0;

          img {
            width: 16px;
            height: 16px;
            filter: brightness(0) invert(1);
            opacity: 0.6;
          }

          &:hover {
            background: rgba(255, 255, 255, 0.08);

            img {
              opacity: 1;
            }
          }
        }
      }
    }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;

    @include below($md) {
      display: none;
    }
  }

  &__mobile {
    display: none;
    align-items: center;
    gap: 16px;
    margin-left: auto;

    @include below($md) {
      display: flex;
    }

    &-search,
    &-close,
    &-burger {
      cursor: pointer;
      display: flex;
      align-items: center;

      img, .icon {
        width: 20px;
        height: 20px;
        filter: brightness(0) invert(1);
      }
    }
  }
}

// ============================================
// UTILITY ITEMS (in primary bar)
// ============================================
.header-utility {
  position: relative;

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.85);
    font-size: 1.2rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 4px;
    white-space: nowrap;
    text-decoration: none;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      color: $white;
    }

    &--icon {
      padding: 8px;
    }

    &--login {
      color: $white;
      text-decoration: none;
      font-size: 1.2rem;
      font-weight: 600;
    }
  }

  &__chevron {
    transition: transform 0.2s ease;
    &.open {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    padding-top: 8px;
    min-width: 200px;
    z-index: 20;
    // padding-top creates an invisible hover bridge between button and menu
    background: transparent;

    &::before {
      // Visible card background
      content: "";
      position: absolute;
      top: 8px;
      left: 0;
      right: 0;
      bottom: 0;
      background: $white;
      border-radius: 6px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08);
    }

    &-current {
      display: block;
      padding: 10px 16px;
      font-size: 1.3rem;
      font-weight: 700;
      color: $corporateBlue;
      background: rgba($corporateBlue, 0.04);
      border-left: 3px solid $corporateBlue;
      position: relative;
      z-index: 1;
    }
  }

  &__dropdown-item {
    display: block;
    padding: 10px 16px;
    font-size: 1.3rem;
    font-weight: 500;
    color: $text;
    text-decoration: none;
    transition: background 0.12s ease;
    cursor: pointer;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    position: relative;
    z-index: 1;

    &:hover {
      background: #f5f6f8;
    }

    &--disabled {
      color: $border;
      cursor: not-allowed;
      &:hover {
        background: none;
      }
    }

    &--logout {
      border-top: 1px solid #e8ebf4;
      color: $light_gray;
      font-weight: 600;
      margin-top: 2px;
      padding-top: 10px;
    }
  }
}

// ============================================
// SECONDARY BAR
// ============================================
.header-secondary {
  background: $white;
  border-bottom: 1px solid #e8ebf4;
  position: relative;
  z-index: 10;

  &__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
  }

  &__nav {
    flex: 1;
  }

  &__list {
    display: flex;
    align-items: center;
    gap: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    height: 48px;

    @include below($md) {
      display: none;
    }
  }

  &__item {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;

    & > a {
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 16px;
      text-transform: uppercase;
      color: $text;
      font-size: 1.4rem;
      font-weight: 700;
      text-decoration: none;
      position: relative;
      white-space: nowrap;
      transition: color 0.15s ease;

      @include below($lg) {
        font-size: 1.2rem;
        padding: 0 10px;
      }

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 16px;
        right: 16px;
        height: 0;
        background: $corporateBlue;
        transition: height 0.15s ease;

        @include below($lg) {
          left: 10px;
          right: 10px;
        }
      }

      &:hover::after {
        height: 2px;
        background: rgba($corporateBlue, 0.3);
      }

      &.nuxt-link-exact-active::after,
      &.nuxt-link-active::after {
        height: 3px;
        background: $corporateBlue;
      }

      &:hover {
        color: $corporateBlue;
      }
    }

    &--restricted {
      position: relative;

      &::after {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        content: "";
        background: rgba(255, 255, 255, 0.7);
        cursor: not-allowed;
        z-index: 1;
      }
    }
  }

  &__submenu {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 3;
    background: $white;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    border-radius: 0 0 6px 6px;
    padding: 8px 0;
    min-width: 180px;
    list-style: none;

    li a {
      display: block;
      padding: 8px 16px;
      font-size: 1.3rem;
      color: $text;
      text-decoration: none;
      white-space: nowrap;
      font-weight: 500;

      &:hover {
        background: #f5f6f8;
        color: $corporateBlue;
      }
    }
  }

  // VIQ link
  &__viq-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    margin-right: 4px;
    text-decoration: none;
    flex-shrink: 0;
    height: 100%;
    position: relative;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.75;
    }

    &.nuxt-link-active::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 12px;
      right: 12px;
      height: 3px;
      background: #5BA89D;
    }
  }

  &__viq-logo {
    height: 16px;
    width: auto;
    display: block;
  }

  // Tools dropdown
  &__tools {
    position: relative;
    flex-shrink: 0;
  }

  &__tools-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: $text;
    font-size: 1.3rem;
    font-weight: 700;
    text-transform: uppercase;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 4px;
    white-space: nowrap;
    transition: background 0.15s ease;

    &:hover {
      background: #f5f6f8;
    }
  }

  &__tools-chevron {
    transition: transform 0.2s ease;
    &.open {
      transform: rotate(180deg);
    }
  }

  &__tools-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    padding-top: 8px;
    min-width: 180px;
    z-index: 20;
    background: transparent;

    &::before {
      content: "";
      position: absolute;
      top: 8px;
      left: 0;
      right: 0;
      bottom: 0;
      background: $white;
      border-radius: 6px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08);
    }
  }

  &__tools-item {
    display: block;
    padding: 10px 16px;
    font-size: 1.3rem;
    font-weight: 500;
    color: $text;
    text-decoration: none;
    transition: background 0.12s ease;
    position: relative;
    z-index: 1;

    &:hover {
      background: #f5f6f8;
    }

    &--disabled {
      color: $border;
      cursor: not-allowed;
      &:hover {
        background: none;
      }
    }
  }

  // Tooltip positioning
  .tip {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 100%;
    z-index: 5;
  }
}

// ============================================
// FULL HEADER
// ============================================
.header {
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 10;
}
</style>
